import { Snackbar } from '@mui/material';
import React, {
    ReactNode,
    createContext,
    useContext,
    useState
} from 'react';

import { muiStyles } from './styles';

type AlertColor = 'success' | 'error' | 'info';

type SnackbarContextType = {
    showSnackbar: (message: string, severity?: AlertColor) => void;
};

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

export const useSnackbar = () => {
    const context = useContext(SnackbarContext);
    if (context === undefined) {
        throw new Error('useSnackbar must be nested under a SnackbarProvider');
    }

    return context;
};

export const SnackbarProvider = ({ children }: { children: ReactNode }) => {
    const classes = muiStyles();
    const [ open, setOpen ] = useState<boolean>(false);
    const [ message, setMessage ] = useState<string>('');
    const [ severity, setSeverity ] = useState<AlertColor>('info');

    const showSnackbar = (msg: string, sev: AlertColor = 'info') => {
        setMessage(msg);
        setSeverity(sev);
        setOpen(true);
    };

    const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }

        setOpen(false);
    };

    const alertClass = (sev: AlertColor) => {
        if (sev === 'success') {
            return classes.notificationSuccess;
        }

        if (sev === 'error') {
            return classes.notificationError;
        }

        return classes.notification;
    };

    return (
        <SnackbarContext.Provider value={{ showSnackbar }}>
            {children}
            <Snackbar
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center'
                }}
                autoHideDuration={5000}
                data-testid='snackbar-snackbarComponent'
                onClose={handleClose}
                open={open}
            >
                <div
                    className={alertClass(severity)}
                    data-testid='snackbar-message'
                >
                    {message}
                </div>
            </Snackbar>
        </SnackbarContext.Provider>
    );
};